import { useMemo, useSyncExternalStore } from 'react';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import { getLocalStorageValue, removeLocalStorageValue, setLocalStorageValue } from 'src/utils/local-storage';

/**
 * Custom event name used to notify same-tab subscribers (the side-nav badge)
 * when the action-center page writes/clears its persisted filter scope.
 * The native `storage` event only fires in *other* tabs/windows; we need
 * an in-process bridge for the writer's own tab to re-render.
 */
const PREFERENCES_CHANGED_EVENT = 'notifications-filter-preferences-changed';

/**
 * Filter state persisted across navigation for the Notifications page (UNO-297).
 *
 * Mirrors the URL-canonical / localStorage-mirrors pattern that PR #8644 set up
 * for Tasks. The two notification filters Alex confirmed in scope are:
 *
 * - `alertTypes`  — multi-select; empty means "no explicit alert-type filter".
 * - `primaryAdvisorIds` — when `null`, the user hasn't expressed a choice (the
 *   default-to-self resolver in `filter-defaults.ts` kicks in). `[]` is the
 *   explicit "all advisors" sentinel. `[ids]` means specific advisors.
 *
 * We only mirror to storage when the user makes an explicit choice. The
 * default-to-self fallback for a first-visit advisor never writes anything to
 * storage; we want subsequent visits to keep hitting that fallback rather than
 * locking the user into "self" the moment they first click into the page.
 */
export type NotificationFilterPreferences = {
  alertTypes: string[];
  primaryAdvisorIds: string[] | null;
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  // Reject arrays: they're objects too, but they're not a meaningful
  // "stored preferences" shape and we don't want them silently coerced.
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Lenient parser: fills missing/invalid fields with defaults so a future schema
 * addition doesn't wipe out everyone's saved filter scope. Returns null only
 * when the stored value isn't an object at all.
 */
function parsePrimaryAdvisorIds(value: unknown): string[] | null {
  if (value === null || value === undefined) return null;
  if (isStringArray(value)) return value;
  return null;
}

export function parsePreferences(data: unknown): NotificationFilterPreferences | null {
  if (!isRecord(data)) return null;
  return {
    alertTypes: isStringArray(data['alertTypes']) ? data['alertTypes'] : [],
    primaryAdvisorIds: parsePrimaryAdvisorIds(data['primaryAdvisorIds']),
  };
}

/** Shape both URL state and stored preferences satisfy — read-only safe. */
type ReadonlyFilterParams = {
  readonly alertTypes: readonly string[];
  readonly primaryAdvisorIds: readonly string[] | null;
};

/** True if any persisted field in `params` differs from its parser default. */
export function paramsCarryExplicitFilterState(params: ReadonlyFilterParams): boolean {
  return params.alertTypes.length > 0 || params.primaryAdvisorIds !== null;
}

export function getNotificationFilterPreferences(): NotificationFilterPreferences | null {
  const raw = getLocalStorageValue({ dataKey: LOCAL_STORAGE_KEYS.NOTIFICATIONS_FILTER_PREFERENCES });
  if (raw === null) return null;
  return parsePreferences(raw);
}

export function setNotificationFilterPreferences(prefs: NotificationFilterPreferences): void {
  setLocalStorageValue<NotificationFilterPreferences>({
    dataKey: LOCAL_STORAGE_KEYS.NOTIFICATIONS_FILTER_PREFERENCES,
    data: prefs,
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PREFERENCES_CHANGED_EVENT));
  }
}

export function clearNotificationFilterPreferences(): void {
  removeLocalStorageValue(LOCAL_STORAGE_KEYS.NOTIFICATIONS_FILTER_PREFERENCES);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PREFERENCES_CHANGED_EVENT));
  }
}

/**
 * Subscribe a same-tab + cross-tab listener to persisted-filter changes.
 * `useSyncExternalStore` requires a string snapshot, so we expose the raw
 * localStorage string and parse it inside the consumer hook via useMemo.
 */
function noopUnsubscribe(): void {
  // SSR / no-window fallback for useSyncExternalStore.
}

function subscribeToPreferencesChanges(callback: () => void): () => void {
  if (typeof window === 'undefined') return noopUnsubscribe;
  function storageListener(event: StorageEvent) {
    if (event.key === null || event.key === LOCAL_STORAGE_KEYS.NOTIFICATIONS_FILTER_PREFERENCES) {
      callback();
    }
  }
  window.addEventListener('storage', storageListener);
  window.addEventListener(PREFERENCES_CHANGED_EVENT, callback);
  return function unsubscribe() {
    window.removeEventListener('storage', storageListener);
    window.removeEventListener(PREFERENCES_CHANGED_EVENT, callback);
  };
}

function getRawPreferencesSnapshot(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(LOCAL_STORAGE_KEYS.NOTIFICATIONS_FILTER_PREFERENCES);
}

function getServerSnapshot(): string | null {
  return null;
}

/**
 * React hook that returns the currently-persisted notification filter
 * preferences, re-rendering when storage changes in this tab or any other.
 * The side-nav badge uses this so its count tracks the effective filter
 * even when the user is on a non-`/co-pilot` route.
 */
export function useNotificationFilterPreferencesValue(): NotificationFilterPreferences | null {
  const raw = useSyncExternalStore(subscribeToPreferencesChanges, getRawPreferencesSnapshot, getServerSnapshot);
  return useMemo(() => {
    if (raw === null) return null;
    try {
      return parsePreferences(JSON.parse(raw));
    } catch {
      return null;
    }
  }, [raw]);
}
